import { createContext, useContext } from "react";
import { action, computed, observable } from "mobx";
import { IMerchantJSON } from "src/interfaces";
import { PassportStore } from "src/stores/Passport.store";
import { cleanObject } from "src/helpers/cleanObject";
import { asyncRequest } from "src/decorators/asyncRequest";
import { AsyncStore } from "src/stores/Async.store";
import { Merchant } from "src/models/Merchant.model";
import { User } from "src/models/User.model";
import { composeFiles } from "src/helpers/composeFiles";

export type IPassportCitizenship = "WNI" | "WNA";

export class OwnerInfoState extends AsyncStore<OwnerInfoState> {
  constructor(
    public translate?: (...args: any) => string,
    public passportStore?: PassportStore
  ) {
    super();
  }

  @observable
  selectedCitizenship: IPassportCitizenship = "WNI";

  @observable
  isIdTypeKitas = this.passportStore?.outlet?.json?.id_type === "KITAS";

  @observable
  citizenshipOptions = [
    {
      id: "WNI",
      key: this.translate?.("owner_info.citizenship.wni"),
      selected: !this.isIdTypeKitas,
    },
    {
      id: "WNA",
      key: this.translate?.("owner_info.citizenship.wna"),
      selected: this.isIdTypeKitas,
    },
  ];

  @observable
  fileUpload: any = {
    ktp: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    kitas: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    passport: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    npwp: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
  };

  @observable
  form: Partial<IMerchantJSON> = {
    director_name: this.sanitizedDirectorName || "",
    id_name: "",
    id_number: "",
    id_type: "",
    npwp: "",
    npwp_name: "",
    npwp_url: "",
    headquarters_address: "",
    files: this.passportStore?.outlet?.files! || [],
  };

  @observable
  errorFields: any = {
    director_name: {
      error: false,
      helperText: undefined,
    },
    id_name: {
      error: this.passportStore?.outlet?.idName ? false : undefined,
      helperText: undefined,
    },
    id_number: {
      error: this.passportStore?.outlet.idNumber ? false : undefined,
      helperText: undefined,
    },
    npwp: {
      error: this.passportStore?.outlet?.npwp ? false : undefined,
      helperText: undefined,
    },
    npwp_name: {
      error: this.passportStore?.outlet?.npwpName ? false : undefined,
      helperText: undefined,
    },
    headquarters_address: {
      error: this.passportStore?.outlet?.headquartersAddress
        ? false
        : undefined,
      helperText: undefined,
    },
    ktp_upload: {
      error: undefined,
      helperText: undefined,
    },
    kitas_upload: {
      error: undefined,
      helperText: undefined,
    },
    passport_upload: {
      error: undefined,
      helperText: undefined,
    },
    npwp_upload: {
      error: this.passportStore?.outlet.npwpUrl ? false : undefined,
      helperText: undefined,
    },
  };

  get sanitizedDirectorName(): string | undefined {
    return this.passportStore?.authUser?.fullName?.replace(/[^a-z A-Z .]/g, "");
  }

  @action
  fillIdFields = (outlet: Merchant) => {
    this.form = {
      ...this.form,
      id_name: outlet.idName,
      id_number: outlet.idNumber,
      id_url: outlet.idUrl,
    };
    this.errorFields = {
      ...this.errorFields,
      id_number: {
        error: false,
      },
      id_name: {
        error: false,
      },
    };
  };

  @action
  fillForm = (outlet: Merchant, user: User) => {
    const npwpFile = outlet?.files?.find((file) =>
      file?.name?.includes(`midtrans.npwp_${outlet.businessType}`)
    );

    this.form = {
      director_name: outlet.json.director_name || user.fullName,
      files: outlet.files,
      npwp: outlet.npwp,
      npwp_name: outlet.npwpName,
      id_type: outlet.json.id_type,
      headquarters_address: outlet.headquartersAddress,
      npwp_url:
        outlet?.files?.find((file) =>
          file?.name?.includes(`midtrans.npwp_${outlet.businessType}`)
        )?.url || outlet.npwpUrl,
    };
    this.fileUpload.npwp.url = npwpFile?.url || outlet.npwpUrl;
    this.fileUpload.npwp.mediaType = npwpFile?.media_type;
    this.fileUpload.npwp.metadata = npwpFile?.metadata;

    outlet?.files?.map((file) => {
      if (file.name === `midtrans.passport`) {
        this.fileUpload.passport.url = file.url;
        this.fileUpload.passport.mediaType = file.media_type;
        this.fileUpload.passport.metadata = file.metadata;
        this.errorFields.passport_upload.error = false;
      } else if (
        file.name?.startsWith(
          `midtrans.ktp_${this.passportStore?.selectedBusinessType!}`
        )
      ) {
        this.fileUpload.ktp.url = file.url;
        this.fileUpload.ktp.mediaType = file.media_type;
        this.fileUpload.ktp.metadata = file.metadata;
        this.errorFields.ktp_upload.error = false;
      } else if (
        file.name?.startsWith(
          `midtrans.kitas_${this.passportStore?.selectedBusinessType!}`
        )
      ) {
        this.fileUpload.kitas.url = file.url;
        this.fileUpload.kitas.mediaType = file.media_type;
        this.fileUpload.kitas.metadata = file.metadata;
        this.errorFields.kitas_upload.error = false;
      }
    });
    this.fillIdFields(outlet);

    if (this.isIdTypeKitas) {
      this.selectedCitizenship = "WNA";
    } else {
      this.selectedCitizenship = "WNI";
    }
  };

  filteredField(keys: string[]) {
    const keysErrorField = Object.keys(this.errorFields);
    let _filteredField: string[] = [];

    keys.forEach((key) => {
      _filteredField = [
        ..._filteredField,
        ...keysErrorField.filter((value) => value === key),
      ];
    });

    return _filteredField;
  }

  get requiredField() {
    let _requiredField = ["director_name", "id_name"];

    if (
      this.isIndividualBasicAndWni ||
      this.isCorporateBasicAndWni ||
      this.isCorporateCardAndWni
    ) {
      _requiredField = [..._requiredField, "id_number", "ktp_upload"];
    }

    if (this.isIndividualBasicAndWna) {
      _requiredField = [..._requiredField, "kitas_upload", "passport_upload"];
    }

    if (this.isIndividualCardAndWni) {
      _requiredField = [
        ..._requiredField,
        "id_number",
        "ktp_upload",
        "npwp",
        "npwp_name",
        "headquarters_address",
        "npwp_upload",
      ];
    }

    if (
      this.isIndividualCardAndWna ||
      this.isCorporateBasicAndWna ||
      this.isCorporateCardAndWna
    ) {
      _requiredField = [..._requiredField, "kitas_upload", "passport_upload"];
    }

    return this.filteredField(_requiredField);
  }

  @computed
  get isFieldNotValid() {
    const errors = this.requiredField.map((key) => {
      if (this.errorFields[key].error !== undefined) {
        return this.errorFields[key].error === true;
      }

      return this.errorFields[key].error;
    });

    const hasError = errors?.includes(true) || errors?.includes(undefined);
    return hasError;
  }

  get isIndividualBasicAndWni() {
    return (
      this.passportStore?.selectedBusinessType === "individual" &&
      this.passportStore?.selectedPaymentMethod === "basic" &&
      this.selectedCitizenship === "WNI"
    );
  }

  get isIndividualBasicAndWna() {
    return (
      this.passportStore?.selectedBusinessType === "individual" &&
      this.passportStore.selectedPaymentMethod === "basic" &&
      this.selectedCitizenship === "WNA"
    );
  }

  get isIndividualCardAndWni() {
    return (
      this.passportStore?.selectedBusinessType === "individual" &&
      this.passportStore?.selectedPaymentMethod === "card" &&
      this.selectedCitizenship === "WNI"
    );
  }

  get isIndividualCardAndWna() {
    return (
      this.passportStore?.selectedBusinessType === "individual" &&
      this.passportStore.selectedPaymentMethod === "card" &&
      this.selectedCitizenship === "WNA"
    );
  }

  get isCorporateBasicAndWni() {
    return (
      this.passportStore?.selectedBusinessType === "corporate" &&
      this.passportStore?.selectedPaymentMethod === "basic" &&
      this.selectedCitizenship === "WNI"
    );
  }

  get isCorporateBasicAndWna() {
    return (
      this.passportStore?.selectedBusinessType === "corporate" &&
      this.passportStore.selectedPaymentMethod === "basic" &&
      this.selectedCitizenship === "WNA"
    );
  }

  get isCorporateCardAndWni() {
    return (
      this.passportStore?.selectedBusinessType === "corporate" &&
      this.passportStore?.selectedPaymentMethod === "card" &&
      this.selectedCitizenship === "WNI"
    );
  }

  get isCorporateCardAndWna() {
    return (
      this.passportStore?.selectedBusinessType === "corporate" &&
      this.passportStore.selectedPaymentMethod === "card" &&
      this.selectedCitizenship === "WNA"
    );
  }

  @computed
  get validForm(): Partial<IMerchantJSON> {
    this.form.files = this.form.files?.filter((file) => {
      const prefixName = "midtrans";
      const isKitas =
        file.name! ===
        `${prefixName}.kitas_${this.passportStore?.selectedBusinessType!}`;
      const isPassport = file.name! === `${prefixName}.passport`;
      const isNpwp =
        file.name! ===
        `${prefixName}.npwp_${this.passportStore?.selectedBusinessType!}`;
      const isKtp =
        file.name! ===
        `${prefixName}.ktp_${this.passportStore?.selectedBusinessType!}`;

      if (
        this.isIndividualBasicAndWni ||
        this.isCorporateBasicAndWni ||
        this.isCorporateCardAndWni
      ) {
        return isKtp;
      }

      if (
        this.isIndividualBasicAndWna ||
        this.isIndividualCardAndWna ||
        this.isCorporateBasicAndWna ||
        this.isCorporateCardAndWna
      ) {
        return isKitas || isPassport;
      }

      if (this.isIndividualCardAndWni) {
        return isKtp || isNpwp;
      }

      return file;
    });

    this.form.id_type = this.selectedCitizenship === "WNI" ? "KTP" : "KITAS";

    return cleanObject(this.form);
  }

  @action
  resetSelectedCitizenship() {
    this.selectedCitizenship = "WNI";
  }

  @action
  applySelectedCitizenship(citizenship: IPassportCitizenship) {
    this.selectedCitizenship = citizenship;
    if (citizenship === "WNI") {
      this.form.id_number = this.passportStore?.outlet?.idNumber;
    } else {
      this.form.id_number = "";
    }
  }

  @action
  handleOnFieldChange(fieldName: keyof IMerchantJSON, value: any) {
    this.form[fieldName] = value;
    this.errorFields[fieldName].error = false;
    this.errorFields[fieldName].helperText = "";
  }

  @action
  validateForm = (key: any, idValidation?: "id_number" | "npwp") => {
    const keyIMerchant = key as keyof IMerchantJSON;
    const isEmptyValue = !this.form[keyIMerchant];
    let helperText = isEmptyValue
      ? this.translate!("generic.input.error.required")
      : undefined;
    let error = isEmptyValue;

    if (idValidation) {
      const idForm = this.form[keyIMerchant]?.toString().trim();
      const validateId = /^\S\d{15,15}$/;
      const validateNpwp = /^\S\d{14,15}$/;
      const isIdFormValid =
        idValidation === "id_number"
          ? validateId.test(idForm!)
          : validateNpwp.test(idForm!);

      if (!isIdFormValid) {
        error = true;
        helperText = isEmptyValue
          ? this.translate!("generic.input.error.required")
          : this.translate!(`owner_info.input.error.${idValidation}`);
      }
    }

    this.errorFields = {
      ...this.errorFields,
      [key]: { helperText, error },
    };
  };

  @action
  deleteFileUpload = (key: string) => {
    this.fileUpload[key].url = "";
    this.fileUpload[key].temp = undefined;
    this.validateFileUpload(key);
  };

  @action
  validateFileUpload = (key: string) => {
    const isEmptyValue = !this.fileUpload[key].url;
    let helperText = isEmptyValue
      ? this.translate!("generic.input.error.required")
      : undefined;
    let error = isEmptyValue;

    this.errorFields = {
      ...this.errorFields,
      [`${key}_upload`]: { helperText, error },
    };
  };

  @action
  applyFileUpload = (file: File, name: string) => {
    this.fileUpload[name].url = this.passportStore?.fetchUrl;
    this.fileUpload[name].temp = file;
    this.validateFileUpload(name);

    const isIncludesKtp = name.toLowerCase().includes("ktp");
    const isIncludesKitas = name.toLowerCase().includes("kitas");
    const isIncludesNpwp = name.toLowerCase().includes("npwp");
    const isIncludePassport = name.toLowerCase().includes("passport");

    if (isIncludesKtp || isIncludesKitas) {
      this.form.id_type = name.toUpperCase();
      this.form.id_url = this.passportStore?.fetchUrl;
    }

    if (isIncludesNpwp) {
      this.form.npwp_url = this.fileUpload.npwp.url;
    }

    const prefixName = `midtrans.${name}`;
    const fileName = isIncludePassport
      ? prefixName
      : `${prefixName}_${this.passportStore?.selectedBusinessType}`;
    this.form.files = composeFiles(this.form.files!, {
      name: fileName,
      url: this.passportStore?.fetchUrl,
      media_type: file.type,
      metadata: { tag: file.name },
    });
  };

  @action
  @asyncRequest("uploadFileKtp")
  async uploadFileKtp(userId: number, file: File) {
    await this.passportStore?.uploadFile(userId, file);
  }

  @action
  @asyncRequest("uploadFileNpwp")
  async uploadFileNpwp(userId: number, file: File) {
    await this.passportStore?.uploadFile(userId, file);
  }

  @action
  @asyncRequest("uploadFileKitas")
  async uploadFileKitas(userId: number, file: File) {
    await this.passportStore?.uploadFile(userId, file);
  }

  @action
  @asyncRequest("uploadFilePassport")
  async uploadFilePassport(userId: number, file: File) {
    await this.passportStore?.uploadFile(userId, file);
  }
}

export const OwnerInfoContext = createContext(new OwnerInfoState());

export const useOwnerInfoState = () => useContext(OwnerInfoContext);
